import Entity from "../core/entity/Entity";
import { clamp } from "../core/util/MathUtil";
import { objectEntries } from "../core/util/ObjectUtils";
import { choose, chooseWeighted, rBool, rUniform } from "../core/util/Random";
import { V, V2d } from "../core/Vector";
import Asteroid from "./asteroids/Asteroid";
import { AsteroidSize } from "./asteroids/AsteroidShapes";
import { BaseEnemy } from "./enemies/BaseEnemy";
import { BomberEnemy } from "./enemies/BomberEnemy";
import { CarrierEnemy } from "./enemies/CarrierEnemy";
import { FighterSquadron } from "./enemies/FighterSquadron";
import { GhostEnemy } from "./enemies/GhostEnemy";
import { InterceptorSwarm } from "./enemies/InterceptorSwarm";
import { SpiralersSwarm } from "./enemies/SpiralersSwarm";
import { ZephyrEnemy } from "./enemies/ZephyrEnemy";
import { LevelEntity } from "./LevelEntity";

// Difficulty progression constants
const MIN_SPAWN_INTERVAL = 1.5; // Minimum time between spawns (seconds)
const BASE_SPAWN_INTERVAL = 3; // Starting spawn interval for level 1 (seconds)
const SPAWN_INTERVAL_DECREASE_PER_LEVEL = 0.1; // How much faster spawning gets per level

// Spawn points progression constants
const BASE_SPAWN_POINTS = 20; // Starting spawn points for level 1
const ADDITIONAL_SPAWN_POINTS_PER_LEVEL = 10; // Additional spawn points per level

// Spawn positioning constants
const SPAWN_MARGIN = 15; // Distance outside viewport to spawn enemies (world units)

// Entity type spawn probabilities (must sum to 1.0)
const ENTITY_SPAWN_WEIGHTS = {
  asteroid: 0.14, // 14% chance
  interceptorSwarm: 0.16, // 16% chance
  spiralersSwarm: 0.16, // 16% chance
  squadron: 0.16, // 16% chance
  ghost: 0.16, // 16% chance
  carrierBomber: 0.16, // 16% chance
  zephyr: 0.06, // 6% chance - rare!
};

// Spawn point costs for each entity type
const SPAWN_POINT_COSTS = {
  asteroid: 1, // Cheapest - environmental hazard
  interceptorSwarm: 3, // Small but multiple enemies
  spiralersSwarm: 4, // Medium group of spiral attackers
  squadron: 4, // Coordinated group of enemies
  ghost: 5, // Medium single enemy - similar to bomber
  bomber: 5, // Powerful single enemy
  zephyr: 6, // High value target
  carrier: 8, // Most expensive - spawns additional enemies
} as const;

const ASTEROID_SIZE_WEIGHTS = [
  [AsteroidSize.SMALL, 0.4],
  [AsteroidSize.MEDIUM, 0.3], // 30% chance
  [AsteroidSize.LARGE, 0.2], // 20% chance
  [AsteroidSize.XL, 0.08], // 8% chance
  [AsteroidSize.XXL, 0.02], // 2% chance
] as const;

// Carrier/Bomber spawn distribution
const CARRIER_VS_BOMBER_RATIO = 0.3; // 30% carriers, 70% bombers when spawning this type

export default class EnemySpawner extends LevelEntity implements Entity {
  id = "enemySpawner";
  tags = ["enemySpawner"];
  private timeSinceLastSpawn: number = 0;
  private spawnInterval: number;
  private spawnPointsRemaining: number;
  private hasDispatchedFinished: boolean = false;

  constructor(level: number = 1) {
    super();

    // Balanced difficulty progression
    this.spawnInterval = clamp(
      BASE_SPAWN_INTERVAL - (level - 1) * SPAWN_INTERVAL_DECREASE_PER_LEVEL,
      MIN_SPAWN_INTERVAL,
      Infinity,
    );
    this.spawnPointsRemaining =
      BASE_SPAWN_POINTS + (level - 1) * ADDITIONAL_SPAWN_POINTS_PER_LEVEL;
  }

  get isFinishedSpawning(): boolean {
    // Can't spawn anything if we don't have enough points for the cheapest entity
    const minCost = Math.min(...Object.values(SPAWN_POINT_COSTS));
    return this.spawnPointsRemaining < minCost;
  }

  get enemiesRemaining(): number {
    const currentEnemyCount = this.getCurrentEnemyCount();
    // Estimate remaining enemies based on average spawn cost
    const avgSpawnCost =
      (SPAWN_POINT_COSTS.asteroid +
        SPAWN_POINT_COSTS.interceptorSwarm +
        SPAWN_POINT_COSTS.spiralersSwarm +
        SPAWN_POINT_COSTS.squadron +
        (SPAWN_POINT_COSTS.bomber * 0.7 + SPAWN_POINT_COSTS.carrier * 0.3)) /
      5;
    const estimatedRemainingSpawns = Math.floor(
      this.spawnPointsRemaining / avgSpawnCost,
    );
    return currentEnemyCount + estimatedRemainingSpawns;
  }

  get spawnPoints(): number {
    return this.spawnPointsRemaining;
  }

  onTick(dt: number) {
    if (!this.game) return;

    // Check if we just finished spawning
    if (this.isFinishedSpawning && !this.hasDispatchedFinished) {
      this.hasDispatchedFinished = true;
      this.dispatch("spawningFinished", {});
      return;
    }

    if (this.isFinishedSpawning) return;

    this.timeSinceLastSpawn += dt;

    // Check if it's time to spawn and we haven't spawned all enemies yet
    if (this.timeSinceLastSpawn >= this.spawnInterval) {
      this.spawnEntity();
      this.timeSinceLastSpawn = 0;
    }
  }

  private getCurrentEnemyCount(): number {
    if (!this.game) return 0;
    return Array.from(this.game.entities.all).filter(
      (entity: Entity) => entity instanceof BaseEnemy,
    ).length;
  }

  private spawnEntity() {
    if (!this.game) return;

    const spawnPosition = this.getRandomSpawnPosition();

    // Try to select an entity type we can afford
    const entityType = this.selectAffordableEntityType();
    if (!entityType) return; // Can't afford anything

    // Spawn the entity and deduct appropriate cost
    switch (entityType) {
      case "asteroid":
        this.spawnPointsRemaining -= SPAWN_POINT_COSTS.asteroid;
        this.spawnAsteroid(spawnPosition);
        break;
      case "interceptorSwarm":
        this.spawnPointsRemaining -= SPAWN_POINT_COSTS.interceptorSwarm;
        this.spawnInterceptorSwarm(spawnPosition);
        break;
      case "spiralersSwarm":
        this.spawnPointsRemaining -= SPAWN_POINT_COSTS.spiralersSwarm;
        this.spawnSpiralersSwarm(spawnPosition);
        break;
      case "squadron":
        this.spawnPointsRemaining -= SPAWN_POINT_COSTS.squadron;
        this.spawnSquadron(spawnPosition);
        break;
      case "ghost":
        this.spawnPointsRemaining -= SPAWN_POINT_COSTS.ghost;
        this.spawnGhost(spawnPosition);
        break;
      case "zephyr":
        this.spawnPointsRemaining -= SPAWN_POINT_COSTS.zephyr;
        this.spawnZephyr(spawnPosition);
        break;
      case "carrierBomber":
        // Deduct cost based on what we actually spawn
        const canAffordCarrier =
          this.spawnPointsRemaining >= SPAWN_POINT_COSTS.carrier;
        const willSpawnCarrier =
          canAffordCarrier && rBool(CARRIER_VS_BOMBER_RATIO);
        this.spawnPointsRemaining -= willSpawnCarrier
          ? SPAWN_POINT_COSTS.carrier
          : SPAWN_POINT_COSTS.bomber;
        this.spawnCarrierOrBomber(spawnPosition);
        break;
    }
  }

  private selectAffordableEntityType():
    | keyof typeof ENTITY_SPAWN_WEIGHTS
    | null {
    // Build weighted options for affordable entity types
    const affordableOptions: Array<
      [keyof typeof ENTITY_SPAWN_WEIGHTS, number]
    > = [];

    for (const [type, weight] of objectEntries(ENTITY_SPAWN_WEIGHTS)) {
      // Check if we can afford this entity type
      if (type === "carrierBomber") {
        // For carrier/bomber, we only need to afford the cheaper option (bomber)
        if (this.spawnPointsRemaining >= SPAWN_POINT_COSTS.bomber) {
          affordableOptions.push([type, weight]);
        }
      } else if (this.spawnPointsRemaining >= SPAWN_POINT_COSTS[type]) {
        affordableOptions.push([type, weight]);
      }
    }

    if (affordableOptions.length === 0) return null;

    return chooseWeighted(affordableOptions);
  }

  private spawnCarrierOrBomber(position: V2d) {
    if (!this.game) return;

    // Check if we have enough points for a carrier
    const canAffordCarrier =
      this.spawnPointsRemaining >= SPAWN_POINT_COSTS.carrier;

    // Decide between carrier and bomber based on ratio and affordability
    const shouldSpawnCarrier =
      canAffordCarrier && rBool(CARRIER_VS_BOMBER_RATIO);
    const enemy = shouldSpawnCarrier
      ? new CarrierEnemy(position)
      : new BomberEnemy(position);

    // Add enemy to the current level
    this.getCurrentLevel()?.addChild(enemy);
  }

  private spawnSquadron(position: V2d) {
    // Create fighter squadron
    const squadron = new FighterSquadron(position);

    // Add squadron to the current level
    this.getCurrentLevel()?.addChild(squadron);
  }

  private spawnAsteroid(position: V2d) {
    const asteroidSize = chooseWeighted(ASTEROID_SIZE_WEIGHTS);

    const directionToCenter = V(0, 0).sub(position);
    const initialSpeed = rUniform(10, 30);
    // Give asteroids some initial velocity toward the center
    const initialVelocity = directionToCenter.normalize(initialSpeed);

    const asteroid = new Asteroid(position, asteroidSize, initialVelocity);

    // Add asteroid to the current level
    this.getCurrentLevel()?.addChild(asteroid);
  }

  private spawnInterceptorSwarm(position: V2d) {
    // Create interceptor swarm
    const swarm = new InterceptorSwarm(position);

    // Add swarm to the current level
    this.getCurrentLevel()?.addChild(swarm);
  }

  private spawnSpiralersSwarm(position: V2d) {
    // Create spiralers swarm
    const swarm = new SpiralersSwarm(position);

    // Add swarm to the current level
    this.getCurrentLevel()?.addChild(swarm);
  }

  private spawnGhost(position: V2d) {
    // Create ghost enemy (spawns invisible, will phase in at random location)
    const ghost = new GhostEnemy(position);

    // Add ghost to the current level
    this.getCurrentLevel()?.addChild(ghost);
  }

  private spawnZephyr(position: V2d) {
    // Create zephyr enemy (high value evasive target)
    const zephyr = new ZephyrEnemy(position);

    // Add zephyr to the current level
    this.getCurrentLevel()?.addChild(zephyr);
  }

  private getRandomSpawnPosition(): V2d {
    if (!this.game) return V(0, 0);

    const viewport = this.game.camera.getWorldViewport();

    const side = choose("top", "right", "bottom", "left");

    switch (side) {
      case "top":
        return V(
          rUniform(viewport.left, viewport.left + viewport.width),
          viewport.top - SPAWN_MARGIN,
        );
      case "right":
        return V(
          viewport.right + SPAWN_MARGIN,
          rUniform(viewport.top, viewport.top + viewport.height),
        );
      case "bottom":
        return V(
          rUniform(viewport.left, viewport.left + viewport.width),
          viewport.bottom + SPAWN_MARGIN,
        );
      case "left":
        return V(
          viewport.left - SPAWN_MARGIN,
          rUniform(viewport.top, viewport.top + viewport.height),
        );
      default:
        throw new Error("Invalid spawn side: " + side);
    }
  }
}
